import { create } from 'zustand';
import { Goal, GoalFormData, GoalList } from '../types/goals';
import { goalsService } from '../services/goalsService';
import { personaService } from '../services/personaService';
import { listsService } from '../services/listsService';
import { universalCompletionService } from '../services/universalCompletionService';

interface GoalsStoreState {
  // State
  goals: Goal[];
  isLoading: boolean;
  error: string | null;

  // Actions
  initializeGoals: () => void;
  createGoal: (data: GoalFormData) => Promise<void>;
  updateGoal: (id: string, updates: Partial<Goal>) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  reorderGoals: (reorderedGoals: Goal[]) => Promise<void>;

  // Goal-specific actions
  updateGoalProgress: (id: string, progress: number) => Promise<void>;

  // List actions
  addList: (goalId: string, list: Omit<GoalList, 'id'>) => Promise<void>;
  updateList: (goalId: string, listId: string, updates: Partial<GoalList>) => Promise<void>;

  // Utility methods
  getGoalById: (id: string) => Goal | undefined;
  getGoalsByAspirationId: (aspirationId: string) => Goal[];
  getGoalsWithCalculatedProgress: (contextDate?: Date) => Goal[];
  getOverdueGoals: () => Goal[];
  getGoalsStats: () => {
    total: number;
    completed: number;
    inProgress: number;
    notStarted: number;
    overdue: number;
  };

  // Error handling
  clearError: () => void;
  setError: (error: string) => void;
}

export const useGoalsStore = create<GoalsStoreState>((set, get) => ({
  // Initial state
  goals: [],
  isLoading: false,
  error: null,

  // Initialize goals
  initializeGoals: () => {
    set({ isLoading: true, error: null });
    try {
      // Ensure persona data is loaded
      personaService.initializeIfNeeded();

      goalsService.initializeMockData();
      const goals = goalsService.getAllGoals();
      set({ goals, isLoading: false });
    } catch (error) {
      console.error('Failed to initialize goals:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to initialize goals',
        isLoading: false
      });
    }
  },

  // Create new goal
  createGoal: async (data: GoalFormData) => {
    set({ isLoading: true, error: null });
    try {
      const newGoal = await goalsService.createGoal(data);
      const updatedGoals = goalsService.getAllGoals();
      set({ goals: updatedGoals, isLoading: false });
      console.log('✅ Goal created successfully:', newGoal.title);
    } catch (error) {
      console.error('Failed to create goal:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to create goal',
        isLoading: false 
      });
      throw error;
    }
  },

  // Update existing goal
  updateGoal: async (id: string, updates: Partial<Goal>) => {
    set({ error: null });
    try {
      const updatedGoal = await goalsService.updateGoal(id, updates);
      const updatedGoals = goalsService.getAllGoals();
      set({ goals: updatedGoals });
      console.log('✅ Goal updated successfully:', updatedGoal.title);
    } catch (error) {
      console.error('Failed to update goal:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update goal' });
      throw error;
    }
  },

  // Delete goal (with cascade delete of linked lists)
  deleteGoal: async (id: string) => {
    set({ error: null });
    try {
      // Cascade delete linked lists first (same pattern as aspirationsStore)
      const linkedLists = listsService.getListsByGoalId(id);
      linkedLists.forEach(list => {
        listsService.deleteList(list.id);
        console.log(`🗑️ Deleted list: ${list.title}`);
      });

      if (linkedLists.length > 0) {
        console.log(`🗑️ Cascade deleted ${linkedLists.length} linked lists`);
      }

      // Then delete the goal
      await goalsService.deleteGoal(id);
      const updatedGoals = goalsService.getAllGoals();
      set({ goals: updatedGoals });
      console.log('✅ Goal deleted successfully');
    } catch (error) {
      console.error('Failed to delete goal:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to delete goal' });
      throw error;
    }
  },

  // Reorder goals (drag and drop)
  reorderGoals: async (reorderedGoals: Goal[]) => {
    try {
      await goalsService.reorderGoals(reorderedGoals);
      set({ goals: reorderedGoals });
      console.log('✅ Goals reordered successfully');
    } catch (error) {
      console.error('Failed to reorder goals:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to reorder goals' });
      throw error;
    }
  },


  // Update goal progress
  updateGoalProgress: async (id: string, progress: number) => {
    try {
      await goalsService.updateGoal(id, { progress });
      const updatedGoals = goalsService.getAllGoals();
      set({ goals: updatedGoals });
    } catch (error) {
      console.error('Failed to update goal progress:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update progress' });
      throw error;
    }
  },

  // Add milestone to goal
  addList: async (goalId: string, list: Omit<GoalList, 'id'>) => {
    try {
      await goalsService.addList(goalId, list);
      const updatedGoals = goalsService.getAllGoals();
      set({ goals: updatedGoals });
      console.log('✅ List added successfully');
    } catch (error) {
      console.error('Failed to add list:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to add list' });
      throw error;
    }
  },

  // Update milestone
  updateList: async (goalId: string, listId: string, updates: Partial<GoalList>) => {
    try {
      await goalsService.updateList(goalId, listId, updates);
      const updatedGoals = goalsService.getAllGoals();
      set({ goals: updatedGoals });
      
      if (updates.completed) {
        console.log('🎯 List completed!');
      }
    } catch (error) {
      console.error('Failed to update list:', error);
      set({ error: error instanceof Error ? error.message : 'Failed to update list' });
      throw error;
    }
  },

  // Utility methods
  getGoalById: (id: string) => {
    return get().goals.find(goal => goal.id === id);
  },

  getGoalsByAspirationId: (aspirationId: string) => {
    return get().goals.filter(goal => goal.aspirationId === aspirationId)
      .sort((a, b) => a.priority - b.priority);
  },

  getGoalsWithCalculatedProgress: (contextDate = new Date()) => {
    return get().goals.map(goal => {
      const lists = listsService.getListsByGoalId(goal.id);

      if (lists.length === 0) {
        return goal;
      }

      let totalItems = 0;
      let completedItems = 0;
      let completedListsCount = 0;

      lists.forEach(list => {
        const listData = universalCompletionService.getListDataForContext(list, contextDate);
        totalItems += listData.totalItems;
        completedItems += listData.completedItems;

        // Count list as completed if all its items are done
        if (listData.progress === 100) {
          completedListsCount++;
        }
      });

      const progress = totalItems === 0 ? 0 : Math.round((completedItems / totalItems) * 100);

      // Auto-update status based on progress
      let status = goal.status;
      if (progress === 100 && status !== 'completed') {
        status = 'completed';
      } else if (progress > 0 && status === 'not_started') {
        status = 'in_progress';
      }

      // Update the lists array with actual completion status
      const updatedLists = lists.map(list => {
        const listData = universalCompletionService.getListDataForContext(list, contextDate);
        return {
          id: list.id,
          title: list.title,
          description: list.description,
          deadline: list.deadline || new Date(),
          completed: listData.progress === 100,
          completedAt: listData.progress === 100 ? new Date() : undefined
        };
      });

      return {
        ...goal,
        progress,
        status,
        lists: updatedLists
      };
    });
  },

  getOverdueGoals: () => {
    const now = new Date();
    return get().goals.filter(goal => 
      goal.deadline && 
      goal.deadline < now && 
      goal.status !== 'completed' &&
      goal.status !== 'cancelled'
    );
  },

  getGoalsStats: () => {
    const goals = get().goals;
    const now = new Date();
    
    return {
      total: goals.length,
      completed: goals.filter(g => g.status === 'completed').length,
      inProgress: goals.filter(g => g.status === 'in_progress').length,
      notStarted: goals.filter(g => g.status === 'not_started').length,
      overdue: goals.filter(g => 
        g.deadline && 
        g.deadline < now && 
        g.status !== 'completed' &&
        g.status !== 'cancelled'
      ).length,
    };
  },

  // Error handling
  clearError: () => set({ error: null }),
  
  setError: (error: string) => set({ error }),
}));